import { CheckCircleOutline, Lock } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { PMPopUp, PMSetupSteps, Tooltip } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ReactNode, useState } from "react";

export interface PMWelcomeScreenProps {
  isOpen: boolean;
  completedItems: {
    hasGeneralData: boolean;
    hasReportingData: boolean;
    hasProjectsAndDeliverables: boolean;
    hasInvitedUsers: boolean;
    hasNotifications: boolean;
  };
  language: string;
  settingsLocked?: boolean;
  lockedMessage?: string;
  projectName: { single: string; plural: string };
  itemName: { single: string; plural: string };
  hasProjects?: boolean;
  deleteSampleProjects: () => Promise<void>;
}

interface ChecklistItem {
  id: string;
  title: string;
  description: string | ((...args: unknown[]) => string | ReactNode);
  completed: boolean;
  buttonText: string | ((...args: unknown[]) => string);
  destination:
    | "settings-general-data"
    | "settings-reporting-data"
    | "settings-project-content"
    | "settings-invite-user"
    | "settings-notifications";
}

type NavigationDestination =
  | "settings-general-data"
  | "settings-reporting-data"
  | "settings-project-content"
  | "settings-invite-user"
  | "settings-notifications";

export const PMWelcomeScreen: PimoReactComponent<
  PMWelcomeScreenProps,
  | "welcome-screen:close"
  | "welcome-screen:navigate"
  | "welcome-screen:skip"
  | "welcome-screen:complete",
  NavigationDestination
> = ({
  isOpen,
  completedItems,
  fireEvent,
  language,
  settingsLocked = false,
  lockedMessage,
  projectName,
  itemName,
  hasProjects = false,
  deleteSampleProjects,
}) => {
  const theme = useTheme();
  const [localCompletedItems, setLocalCompletedItems] =
    useState(completedItems);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const handleConfirmationClose = () => {
    setIsConfirmationOpen(false);
  };

  const [prevCompletedItems, setPrevCompletedItems] = useState(completedItems);
  if (prevCompletedItems !== completedItems) {
    setPrevCompletedItems(completedItems);
    setLocalCompletedItems(completedItems);
  }

  const totalSteps = [
    localCompletedItems.hasGeneralData,
    localCompletedItems.hasReportingData,
    localCompletedItems.hasProjectsAndDeliverables,
    localCompletedItems.hasInvitedUsers,
    localCompletedItems.hasNotifications,
  ];
  const totalCount = totalSteps.length;
  const completedCount = totalSteps.filter(Boolean).length;

  const checklistItems: ChecklistItem[] = [
    {
      id: "set-general-data",
      title: getLocalizedMessage(language, "setupSteps.generalDataTitle"),
      description: getLocalizedMessage(
        language,
        "setupSteps.generalDataDescription"
      ),
      completed: localCompletedItems.hasGeneralData,
      destination: "settings-general-data",
      buttonText: getLocalizedMessage(language, "setupSteps.edit"),
    },
    {
      id: "set-reporting-data",
      title: getLocalizedMessage(language, "setupSteps.reportingDataTitle"),
      description: getLocalizedMessage(
        language,
        "setupSteps.reportingDataDescription"
      ),
      completed: localCompletedItems.hasReportingData,
      destination: "settings-reporting-data",
      buttonText: getLocalizedMessage(language, "setupSteps.edit"),
    },
    {
      id: "create-project-content",
      title: getLocalizedMessage(language, "setupSteps.contentTitle", {
        projectsName: projectName.plural,
        itemsName: itemName.plural,
      }),
      description: (hasSamples) =>
        hasSamples
          ? getLocalizedMessage(
              language,
              "setupSteps.contentDescriptionWithSamples",
              { projectsName: projectName.plural }
            )
          : getLocalizedMessage(
              language,
              "setupSteps.contentDescriptionWithoutSamples",
              { projectName: projectName.single }
            ),
      completed: localCompletedItems.hasProjectsAndDeliverables,
      destination: "settings-project-content",
      buttonText: (hasSamples) =>
        hasSamples
          ? getLocalizedMessage(language, "setupSteps.deleteProjects", {
              projectsName: projectName.plural,
            })
          : getLocalizedMessage(language, "setupSteps.createProject", {
              projectName: projectName.single,
            }),
    },
    {
      id: "invite-user",
      title: getLocalizedMessage(language, "setupSteps.userManagementTitle"),
      description: getLocalizedMessage(
        language,
        "setupSteps.userManagementDescription"
      ),
      completed: localCompletedItems.hasInvitedUsers,
      destination: "settings-invite-user",
      buttonText: getLocalizedMessage(language, "setupSteps.invite"),
    },
    {
      id: "configure-notifications",
      title: getLocalizedMessage(language, "setupSteps.notificationsTitle"),
      description: getLocalizedMessage(
        language,
        "setupSteps.notificationsDescription"
      ),
      completed: localCompletedItems.hasNotifications,
      destination: "settings-notifications",
      buttonText: getLocalizedMessage(language, "setupSteps.edit"),
    },
  ];

  const handleClose = () => {
    fireEvent?.("welcome-screen:close");
  };

  const handleGotoSettings = (item: ChecklistItem) => {
    fireEvent?.("welcome-screen:navigate", item.destination);
  };

  return (
    <PMPopUp open={isOpen} title="" onClose={handleClose} maxWidth="lg">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: theme.palette.text.secondary,
            flex: 3,
          }}
        >
          {`${getLocalizedMessage(language, "setupSteps.finishYourSetup")} (${completedCount}/${totalCount})`}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <PMSetupSteps
            title={getLocalizedMessage(language, "setupSteps.steps")}
            totalSteps={totalCount}
            completedSteps={completedCount}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          maxHeight: "500px",
          overflowY: "auto",
          paddingRight: 1,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.grey[100],
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.grey[400],
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: theme.palette.grey[500],
            },
          },
        }}
      >
        {checklistItems.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              padding: 1.5,
              borderRadius: 6,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: "transparent",
              transition: "all 0.2s ease",
              "&:hover": !item.completed
                ? {
                    backgroundColor: theme.palette.action.hover,
                    borderColor: theme.palette.primary.main,
                  }
                : {},
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: theme.palette.text.primary,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  color: theme.palette.text.primary,
                }}
              >
                {typeof item.description === "function"
                  ? item.description(hasProjects)
                  : item.description}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "12px",
                minHeight: "12px",
                gap: 4,
                ...(item.id === "create-project-content" && hasProjects
                  ? { flexShrink: 0 }
                  : {}),
              }}
            >
              <CheckCircleOutline
                sx={{
                  color: item.completed
                    ? theme.palette.success.main
                    : theme.palette.grey["300"],
                  fontSize: 28,
                }}
              />
              <Typography
                sx={{
                  color: settingsLocked ? "#00000042" : "#38BDF8",
                  fontWeight: "bold",
                  cursor: settingsLocked ? "default" : "pointer",
                  textAlign: "end",
                  // Reserve the full first word beside the checkmark while keeping the second line.
                  width:
                    item.id === "create-project-content" && hasProjects
                      ? "min-content"
                      : "50px",
                }}
                onClick={() => {
                  if (
                    hasProjects &&
                    item.destination === "settings-project-content"
                  ) {
                    setIsConfirmationOpen(true);
                    return;
                  }
                  return !settingsLocked && handleGotoSettings(item);
                }}
              >
                <Tooltip title={settingsLocked ? lockedMessage : undefined}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    {settingsLocked && (
                      <Lock sx={{ fontSize: 16, marginRight: 0.5 }} />
                    )}
                    {typeof item.buttonText === "function"
                      ? item.buttonText(hasProjects)
                      : item.buttonText}
                  </Box>
                </Tooltip>
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <PMPopUp
        open={isConfirmationOpen}
        title={getLocalizedMessage(language, "dialog.title.removeSamples")}
        description={getLocalizedMessage(
          language,
          "dialog.message.removeSamples"
        )}
        onClose={handleConfirmationClose}
        maxWidth="sm"
        cancelButtonText={getLocalizedMessage(
          language || "en",
          "dialog.button.cancel"
        )}
        cancelButtonFireEvent={handleConfirmationClose}
        cancelButtonColor="white"
        confirmationButtonColor="blue"
        confirmationButtonText={getLocalizedMessage(
          language || "en",
          "dialog.button.startSetup"
        )}
        confirmationButtonFireEvent={async () => {
          deleteSampleProjects();
          handleConfirmationClose();
        }}
      ></PMPopUp>
    </PMPopUp>
  );
};
